import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import type {
  AuditKeyApproveRequest,
  AuditKeyApproveResult,
  RevokeRequest,
  RevokeResult,
  SourceInput,
  SourceKeyInput,
  SourceKeyResult,
  SourcePage,
  SourceResult,
  SourceStatus,
} from "./vocab.ts";

/**
 * The reads AND writes for the Sources screen (task 11.14 / T9.14; design 11 operator-API Sources
 * row, 12.2 Sources row, design 13). All shapes come from `@oathera/contract/api` via `vocab.ts`;
 * nothing is redefined.
 *
 * READS (role `viewer`), through `lib/api.ts` so a cold-core 503 surfaces as a `waking` ApiError:
 * - `GET /sources` → {@link SourcePage}: the registered source REGISTRY — status, last seq, drops,
 *   skew, environment, service, admin link, jwks url, telemetry keys and audit-key pins. NOT a
 *   windowed read: the registry is current state, not a time window. The screen prefers the live
 *   stream's per-source liveness (`useStreamView().view.sources`) for status FRESHNESS, and this REST
 *   read for the registry, keys and pins (design 12.3: live for liveness, REST for the registry).
 *
 * WRITES (role `admin`) via TanStack `useMutation`, each INVALIDATING the sources list on success so
 * the screen refetches the authoritative registry. Each mutation surfaces its `ApiError` to the
 * caller (a 403 → "the server did not permit this" — admin required; a 400/422 → the server's
 * validation message, e.g. a rejected JWK). The query client already retries a mutation only when
 * the core is waking.
 */

/* ---- Query keys ----------------------------------------------------------------------------- */

const KEY = {
  /** The sources list, optionally filtered by status. */
  sources: (status: SourceStatus | "all") => ["sources", status] as const,
  /** The base prefix every sources query shares, so a write can invalidate them all at once. */
  all: ["sources"] as const,
};

/* ---- Reads ---------------------------------------------------------------------------------- */

export interface UseSourcesOptions {
  /** Filter to one lifecycle status, or omit for all. */
  readonly status?: SourceStatus;
  /** Page size; contract default applies when omitted. */
  readonly limit?: number;
  /** When false the read does not run (role below viewer, or the shell is waking). Default true. */
  readonly enabled?: boolean;
  /** When false the list does not poll (the time-range is paused). Default true. */
  readonly live?: boolean;
}

/**
 * Fetch one page of registered sources (viewer). The registry is current state, so the query key
 * carries only the status filter. When `live`, it tolerates slight staleness and polls on a slow
 * interval (the fast state changes arrive on the stream); when paused, it never goes stale and does
 * not poll, matching the shared time-range semantics the other screens use.
 */
export function useSources(options: UseSourcesOptions = {}): UseQueryResult<SourcePage, ApiError> {
  const enabled = options.enabled ?? true;
  const live = options.live ?? true;
  const status = options.status ?? "all";

  return useQuery<SourcePage, ApiError>({
    queryKey: KEY.sources(status),
    queryFn: ({ signal }) =>
      apiFetch<SourcePage>("/sources", {
        query: {
          status: options.status,
          limit: options.limit,
        },
        signal,
      }),
    enabled,
    staleTime: live ? 15_000 : Infinity,
    refetchInterval: live ? 30_000 : false,
  });
}

/* ---- Writes (admin) ------------------------------------------------------------------------- */

/**
 * Create (enrol) a source (`POST /sources`, admin). The tenant is NEVER in the body — the server
 * binds it from the verified identity. On success invalidate every sources query so the new source
 * appears with its generated id.
 */
export function useCreateSource(): UseMutationResult<SourceResult, ApiError, SourceInput> {
  const qc = useQueryClient();
  return useMutation<SourceResult, ApiError, SourceInput>({
    mutationFn: (body) => apiFetch<SourceResult>("/sources", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.all, exact: false });
    },
  });
}

/**
 * Register a PUBLIC telemetry key (rotation) OR pin an audit signing key for a source
 * (`POST /sources/{id}/keys`, admin). The body is a `SourceKeyInput` discriminated by `purpose`:
 * `telemetry` carries ONLY a public JWK (never a private key — enforced in the form and in `vocab`),
 * `audit` carries a `kid` + JWK thumbprint. On success invalidate the sources list so the new key /
 * pin and any retired key appear.
 */
export function useRegisterSourceKey(): UseMutationResult<
  SourceKeyResult,
  ApiError,
  { readonly id: string; readonly body: SourceKeyInput }
> {
  const qc = useQueryClient();
  return useMutation<SourceKeyResult, ApiError, { id: string; body: SourceKeyInput }>({
    mutationFn: ({ id, body }) =>
      apiFetch<SourceKeyResult>(`/sources/${encodeURIComponent(id)}/keys`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.all, exact: false });
    },
  });
}

/**
 * Revoke a source, or one of its telemetry keys (`POST /sources/{id}/revoke`, admin). A `kid` in the
 * body scopes the revocation to one key; its absence revokes the whole source. The result carries
 * the 30 s propagation window (FR-11.3). On success invalidate the sources list so the revoked state
 * / retired key appears.
 */
export function useRevokeSource(): UseMutationResult<
  RevokeResult,
  ApiError,
  { readonly id: string; readonly body: RevokeRequest }
> {
  const qc = useQueryClient();
  return useMutation<RevokeResult, ApiError, { id: string; body: RevokeRequest }>({
    mutationFn: ({ id, body }) =>
      apiFetch<RevokeResult>(`/sources/${encodeURIComponent(id)}/revoke`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.all, exact: false });
    },
  });
}

/**
 * Approve an observed, unpinned audit signing key (`POST /audit-keys/{id}/approve`, admin). The `id`
 * is the opaque `AuditKeyPin.id`. The server returns 202: pinning the key QUEUES retroactive
 * re-verification of every stored batch of the chain signed by it (FR-6.6) — valid batches upgrade,
 * invalid batches keep their exact failure. The result's `reverification` reports the queued job;
 * the pin's `reverification` reports its progress thereafter. On success invalidate the sources list
 * so the pin flips to `pinned` and the re-verification progress surfaces.
 */
export function useApproveAuditKey(): UseMutationResult<
  AuditKeyApproveResult,
  ApiError,
  { readonly id: string; readonly body: AuditKeyApproveRequest }
> {
  const qc = useQueryClient();
  return useMutation<AuditKeyApproveResult, ApiError, { id: string; body: AuditKeyApproveRequest }>({
    mutationFn: ({ id, body }) =>
      apiFetch<AuditKeyApproveResult>(`/audit-keys/${encodeURIComponent(id)}/approve`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY.all, exact: false });
    },
  });
}
